import type { ApplicationContext } from './styleApplicator.types';
import type { Canvas, FrameSprites, Sprite, ThemeOutput } from './themeOutput.types';

/**
 * Canvas/sprite selection, split out of useThemeCanvas so the server can reuse it (PRNT-4898).
 *
 * The SSR preload links must name exactly the sprites the client will render, and the only way to
 * guarantee that is to run the same selection. Kept free of React and of every other import on
 * purpose: `src/server` pulls this in, and a hook module drags the feature-flag provider, the
 * fetch layer and env config onto the server with it.
 */

/**
 * Mirrors the legacy getSpritesForAspectRatio: the aspect-ratio switch was commented out in
 * theme-system ("Waiting for sprites to appear in more than one Frame"), so selection is
 * simply the first non-empty frame set. Real theme JSONs populate exactly one set per canvas.
 */
export const pickFrameSprites = (sprites?: Partial<FrameSprites<Sprite>>): Sprite[] => {
  // Each frame set is read optionally: a canvas can arrive with `sprites` absent, empty, or
  // carrying only one of the three keys, and this runs inside the <head> render where a TypeError
  // costs the whole server response, not just the art.
  return [sprites?.horizontal, sprites?.vertical, sprites?.square].find(set => set?.length) ?? [];
};

/**
 * Reads defensively at every level: an SSR-seeded payload is pruned to the canvases its layout
 * renders, and old published themes predate parts of this schema. A missing canvas means "not
 * seeded" — the caller then fetches — never a crash.
 */
export const pickCanvas = (data: ThemeOutput, context: ApplicationContext): Canvas | undefined => {
  if ('component' in context) {
    return context.component === 'title-banner' ? data.components?.['title-banner'] : undefined;
  }
  if (context.composition === 'stacked') {
    // `content` belongs to `simple` alone, so it can only ever miss here — named rather than
    // indexed blindly, which is what the old `Partial<Record<string, Canvas>>` widening hid.
    return context.layer === 'content' ? undefined : data.compositions?.stacked?.layers?.[context.layer];
  }
  if (context.composition === 'simple') {
    // The mirror of the stacked guard above: `simple` carries only `content`, so any other layer
    // can only miss. Returning `content` regardless would hand back the wrong canvas.
    return context.layer === 'content' ? data.compositions?.simple?.layers?.content : undefined;
  }
  return undefined;
};
